import type { Tutorial } from '../types'

const IMG = '/images/tutorials/mobile/multi-profile'

export const multiProfileTutorial: Tutorial = {
  slug: 'multi-profile',
  title: 'Multi-Profile Management',
  subtitle: 'One account, multiple people — switch in one tap',
  date: '2026-07-09',
  category: 'Account',
  platform: 'mobile',
  tags: ['profiles', 'caregiver', 'family', 'switching'],
  description:
    'Hold profiles for a spouse, a parent, or a second profile of your own. Every feature operates on whichever profile is currently active.',
  highlights: [
    'Manage profiles shows genetics, records, and sharing status for everyone on the account.',
    'Profile pill opens a quick-switch sheet from Coach, Goals, or Records — no settings dig.',
    'Switching is instant: Coach context, goals list, and records all scope to the new profile.',
  ],
  coverImage: `${IMG}/01_manage_profiles.png`,
  sections: [
    {
      heading: 'Manage profiles',
      paragraphs: [
        '**Manage profiles** lists everyone under the account with their relationship, age, and a quick summary of what\'s set up for them — genetics uploaded, health records started, sharing/collaborators.',
        'One profile is always marked **Default**, and whichever one is currently in use shows **Active**.',
      ],
      image: {
        src: `${IMG}/01_manage_profiles.png`,
        alt: 'Manage profiles list with multiple family profiles',
        caption: 'Manage profiles list',
      },
    },
    {
      heading: 'Switching profiles',
      paragraphs: [
        'The profile pill at the top of Coach, Goals, and Records opens a quick-switch sheet from anywhere in the app. Tap a profile to switch immediately; press and hold to jump straight to that profile\'s details, sharing, and settings.',
        'Switching is instant and scopes everything — the Coach\'s context, the goals list, the health records shown — to the newly active profile. No confirmation step and no reload.',
      ],
      image: {
        src: `${IMG}/02_switch_profile_sheet.png`,
        alt: 'Switch profile sheet with profile list',
        caption: 'Switch profile sheet',
      },
    },
  ],
  whyMatters:
    'A lot of health apps assume one person, one account. Livewello\'s account model assumes the opposite is common — someone managing wellness for a spouse, tracking a parent\'s health records, or maintaining separate profiles for different purposes — and makes switching between them a one-tap action from any screen.',
  relatedSlugs: ['profile-sharing', 'wellness-coach'],
  meta: {
    description:
      'Multi-profile management in Livewello: manage family profiles and switch between them in one tap from any screen.',
    ogImage: `${IMG}/01_manage_profiles.png`,
  },
}
